import type { ExtractionConfidence } from "../types";

// Tiny confidence indicator — one dot per metadata field. Rendered
// next to the date / from / to / subject in expanded record views so
// the user can spot records whose date isn't trustworthy without
// auditing every single one. Hover shows the extraction method.

type Field = "date" | "from" | "to" | "subject";

interface Props {
  confidence: ExtractionConfidence | null;
  field: Field;
  // Hide when the field isn't present on the record so we don't show
  // a dot next to nothing.
  hidden?: boolean;
}

function tone(
  confidence: ExtractionConfidence,
  field: Field,
): "green" | "amber" | "red" {
  if (field === "date") {
    if (
      confidence.date === "regex_header" ||
      confidence.date === "regex_single"
    ) {
      return "green";
    }
    if (confidence.date === "ai_selected") return "amber";
    return "red"; // fallback
  }
  const v = confidence[field];
  if (v === "header") return "green";
  if (v === "ai") return "amber";
  return "red"; // none
}

function label(
  confidence: ExtractionConfidence,
  field: Field,
): string {
  if (field === "date") {
    switch (confidence.date) {
      case "regex_header":
        return "Extracted from email header (high confidence)";
      case "regex_single":
        return "Extracted by regex — only date found in content";
      case "ai_selected":
        return "AI selected from multiple dates found in content";
      case "fallback":
      default:
        return "No date found in content — using filing date. Click to correct.";
    }
  }
  const v = confidence[field];
  if (v === "header") return "Extracted from source headers (high confidence)";
  if (v === "ai") return "Extracted by AI from source content";
  return "Not found in source";
}

export default function ConfidenceDot({ confidence, field, hidden }: Props) {
  if (hidden) return null;
  if (!confidence) return null;
  const t = tone(confidence, field);
  return (
    <span
      className={`confidence-dot confidence-${t}`}
      title={label(confidence, field)}
      role="img"
      aria-label={`Confidence: ${t === "green" ? "high" : t === "amber" ? "medium" : "low"}`}
    />
  );
}
